// Mockup tasarim alani CARPITMA (warp) — sunucu (sharp) ve tarayici (Konva)
// AYNI kodu kullanir, iki render ayni sonucu verir.
//
// Alan once kendi w x h karosunda hazirlanir (sigdirma, sekil kirpma, opaklik),
// sonra bu karo hedef gorsele carpitilarak basilir:
//   - cylinder  : kupa, termos, cam bardak, mum kavanozu. Tasarim silindirin
//                 gorunen yayina sarilir (kenarlara dogru sikisir); "bend" ile
//                 ustten/alttan bakista yatay cizgiler elips yayi gibi egilir.
//   - perspective: paspas, mat, tabak, kutu kapagi. Dort kose ayri ayri
//                 surulur (alanin kendi koselerine gore piksel ofseti).
// Cizim TERS esleme ile yapilir: hedefteki her piksel icin karodaki (u,v)
// bulunur ve bilinear orneklenir — delik/bosluk birakmaz.

export type AreaWarp =
  | {
      type: "cylinder";
      /** gorunen yay, derece (0 = duz, 180 = yarim silindir) */
      arc: number;
      /** -0.5..0.5, alan yuksekligine oranla yay sarkmasi (+ = ustten bakis). Eski kayitlar: ust = alt = bend */
      bend?: number;
      /** ust ve alt kenarin AYRI sarkmasi (kamera ustten bakarken alt kenar daha kavisli) */
      bendTop?: number;
      bendBottom?: number;
      /** alt genislik / ust genislik (1 = duz silindir, <1 alta dogru incelen termos) */
      taper?: number;
    }
  | { type: "perspective"; /** TL, TR, BR, BL koselerine eklenen ofset (gorsel pikseli) */ corners: [number, number][] };

export interface WarpAreaInput {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation?: number;
  warp?: AreaWarp | null;
}

export interface WarpMap {
  /** karo (u,v) [0..1] -> gorsel pikseli */
  forward(u: number, v: number): [number, number];
  /** gorsel pikseli -> karo (u,v); alan disindaysa null */
  inverse(x: number, y: number): [number, number] | null;
  /** carpitilmis alanin gorseldeki sinir kutusu (tam sayi) */
  bounds: { left: number; top: number; width: number; height: number };
}

export function hasWarp(a: { warp?: AreaWarp | null }): boolean {
  if (!a.warp) return false;
  if (a.warp.type === "cylinder") {
    const c = cylinderParams(a.warp);
    return c.arc > 0.5 || Math.abs(c.bendTop) > 0.001 || Math.abs(c.bendBottom) > 0.001 || Math.abs(c.taper - 1) > 0.001;
  }
  return Array.isArray(a.warp.corners) && a.warp.corners.length === 4 && a.warp.corners.some(([dx, dy]) => dx !== 0 || dy !== 0);
}

/** Silindir parametreleri (eski "bend" alanini ust/alt'a dagitir) */
export function cylinderParams(w: { arc?: number; bend?: number; bendTop?: number; bendBottom?: number; taper?: number }) {
  const b = w.bend ?? 0;
  return {
    arc: Math.max(0, Math.min(179, w.arc ?? 0)),
    bendTop: Math.max(-0.5, Math.min(0.5, w.bendTop ?? b)),
    bendBottom: Math.max(-0.5, Math.min(0.5, w.bendBottom ?? b)),
    taper: Math.max(0.5, Math.min(1.5, w.taper ?? 1)),
  };
}

/** Dondurulmus alanin 4 kosesi (TL, TR, BR, BL) gorsel pikselinde */
export function areaCorners(a: WarpAreaInput): [number, number][] {
  const rad = ((a.rotation || 0) * Math.PI) / 180, c = Math.cos(rad), s = Math.sin(rad);
  return ([[0, 0], [a.w, 0], [a.w, a.h], [0, a.h]] as const).map(([lx, ly]) => [a.x + lx * c - ly * s, a.y + lx * s + ly * c]);
}

/** Perspektif dortgeni: alan koseleri + ofsetler */
export function perspectiveQuad(a: WarpAreaInput): [number, number][] {
  const base = areaCorners(a);
  const off = a.warp?.type === "perspective" ? a.warp.corners : [];
  return base.map(([x, y], i) => [x + (off[i]?.[0] ?? 0), y + (off[i]?.[1] ?? 0)]);
}

/** Birim kare -> dortgen homografisi (Heckbert). [a,b,c,d,e,f,g,h]:
 *  x = (a u + b v + c) / (g u + h v + 1), y = (d u + e v + f) / (g u + h v + 1) */
function squareToQuad(q: [number, number][]): number[] {
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = q;
  const dx3 = x0 - x1 + x2 - x3, dy3 = y0 - y1 + y2 - y3;
  if (Math.abs(dx3) < 1e-9 && Math.abs(dy3) < 1e-9) {
    return [x1 - x0, x3 - x0, x0, y1 - y0, y3 - y0, y0, 0, 0];
  }
  const dx1 = x1 - x2, dx2 = x3 - x2, dy1 = y1 - y2, dy2 = y3 - y2;
  const den = dx1 * dy2 - dx2 * dy1;
  const g = (dx3 * dy2 - dx2 * dy3) / den;
  const h = (dx1 * dy3 - dx3 * dy1) / den;
  return [x1 - x0 + g * x1, x3 - x0 + h * x3, x0, y1 - y0 + g * y1, y3 - y0 + h * y3, y0, g, h];
}

function boundsOf(fwd: (u: number, v: number) => [number, number]) {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const N = 48;
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    for (const [u, v] of [[t, 0], [t, 1], [0, t], [1, t]] as const) {
      const [x, y] = fwd(u, v);
      if (x < x0) x0 = x; if (x > x1) x1 = x;
      if (y < y0) y0 = y; if (y > y1) y1 = y;
    }
  }
  const left = Math.floor(x0), top = Math.floor(y0);
  return { left, top, width: Math.max(1, Math.ceil(x1) - left), height: Math.max(1, Math.ceil(y1) - top) };
}

/** Alanin carpitma eslemesi (warp yoksa duz/dondurulmus dikdortgen) */
export function warpMapOf(a: WarpAreaInput): WarpMap {
  const w = Math.max(1, a.w), h = Math.max(1, a.h);
  const rad = ((a.rotation || 0) * Math.PI) / 180, c = Math.cos(rad), s = Math.sin(rad);
  const toImg = (lx: number, ly: number): [number, number] => [a.x + lx * c - ly * s, a.y + lx * s + ly * c];
  const toLocal = (x: number, y: number): [number, number] => {
    const dx = x - a.x, dy = y - a.y;
    return [dx * c + dy * s, -dx * s + dy * c];
  };

  if (a.warp?.type === "perspective") {
    const [A, B, C, D, E, F, G, H] = squareToQuad(perspectiveQuad(a));
    // Ters matris (3x3, adjugate)
    const i00 = E - F * H, i01 = C * H - B, i02 = B * F - C * E;
    const i10 = F * G - D, i11 = A - C * G, i12 = C * D - A * F;
    const i20 = D * H - E * G, i21 = B * G - A * H, i22 = A * E - B * D;
    const forward = (u: number, v: number): [number, number] => {
      const z = G * u + H * v + 1;
      return [(A * u + B * v + C) / z, (D * u + E * v + F) / z];
    };
    const inverse = (x: number, y: number): [number, number] | null => {
      const z = i20 * x + i21 * y + i22;
      if (Math.abs(z) < 1e-12) return null;
      const u = (i00 * x + i01 * y + i02) / z, v = (i10 * x + i11 * y + i12) / z;
      return u < 0 || u > 1 || v < 0 || v > 1 ? null : [u, v];
    };
    return { forward, inverse, bounds: boundsOf(forward) };
  }

  if (a.warp?.type === "cylinder") {
    // Ust/alt kenar ayri sarkar (arasi dogrusal), genislik alta dogru taper ile
    // degisir (konik termos). Merkez ekseni alanin ortasinda kalir.
    const cp = cylinderParams(a.warp);
    const phi = (cp.arc * Math.PI) / 180;
    const half = Math.sin(phi / 2);
    const bendAt = (v: number) => (cp.bendTop * (1 - v) + cp.bendBottom * v) * h;
    const scaleAt = (v: number) => 1 + (cp.taper - 1) * v;
    const forward = (u: number, v: number): [number, number] => {
      const xn = phi > 0.01 ? Math.sin((u - 0.5) * phi) / half : u * 2 - 1;
      return toImg(w / 2 + (xn * w * scaleAt(v)) / 2, v * h + bendAt(v) * Math.sqrt(Math.max(0, 1 - xn * xn)));
    };
    const inverse = (x: number, y: number): [number, number] | null => {
      const [lx, ly] = toLocal(x, y);
      // v hem genisligi hem sarkmayi etkiler: birkac adimda yakinsar
      let v = ly / h, xn = 0;
      for (let k = 0; k < 5; k++) {
        xn = (lx - w / 2) / ((w * scaleAt(Math.max(0, Math.min(1, v)))) / 2);
        if (xn < -1.0001 || xn > 1.0001) return null;
        xn = Math.max(-1, Math.min(1, xn));
        v = (ly - bendAt(Math.max(0, Math.min(1, v))) * Math.sqrt(Math.max(0, 1 - xn * xn))) / h;
      }
      if (v < 0 || v > 1) return null;
      const u = phi > 0.01 ? Math.asin(Math.max(-1, Math.min(1, xn * half))) / phi + 0.5 : (xn + 1) / 2;
      return u < 0 || u > 1 ? null : [u, v];
    };
    return { forward, inverse, bounds: boundsOf(forward) };
  }

  const forward = (u: number, v: number) => toImg(u * w, v * h);
  const inverse = (x: number, y: number): [number, number] | null => {
    const [lx, ly] = toLocal(x, y);
    const u = lx / w, v = ly / h;
    return u < 0 || u > 1 || v < 0 || v > 1 ? null : [u, v];
  };
  return { forward, inverse, bounds: boundsOf(forward) };
}

/**
 * RGBA karoyu (tw x th) alanin carpitmasiyla gorsele basilacak parcaya cevirir.
 * Donen data, bounds kutusunun RGBA pikselleri (disarisi saydam).
 * Kenarlar yumusak: karonun disina tasan ornek saydam kabul edilir.
 */
export function warpTileRGBA(
  tile: ArrayLike<number>,
  tw: number,
  th: number,
  a: WarpAreaInput,
): { data: Uint8ClampedArray; left: number; top: number; width: number; height: number } {
  const map = warpMapOf(a);
  const { left, top, width, height } = map.bounds;
  const out = new Uint8ClampedArray(width * height * 4);
  const px = (xx: number, yy: number, ch: number) =>
    xx < 0 || yy < 0 || xx >= tw || yy >= th ? 0 : tile[(yy * tw + xx) * 4 + ch];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const uv = map.inverse(left + x + 0.5, top + y + 0.5);
      if (!uv) continue;
      const sx = uv[0] * tw - 0.5, sy = uv[1] * th - 0.5;
      const x0 = Math.floor(sx), y0 = Math.floor(sy);
      const fx = sx - x0, fy = sy - y0;
      const o = (y * width + x) * 4;
      // Alfa-agirlikli bilinear: saydam komsunun rengi kenara sizmasin
      let r = 0, g = 0, b = 0, al = 0;
      const wts: [number, number, number][] = [[x0, y0, (1 - fx) * (1 - fy)], [x0 + 1, y0, fx * (1 - fy)], [x0, y0 + 1, (1 - fx) * fy], [x0 + 1, y0 + 1, fx * fy]];
      for (const [xx, yy, wt] of wts) {
        const aa = px(xx, yy, 3) * wt;
        r += px(xx, yy, 0) * aa; g += px(xx, yy, 1) * aa; b += px(xx, yy, 2) * aa; al += aa;
      }
      if (al <= 0) continue;
      out[o] = r / al; out[o + 1] = g / al; out[o + 2] = b / al; out[o + 3] = al;
    }
  }
  return { data: out, left, top, width, height };
}
